import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { useSEO, breadcrumbSchema, SITE_URL } from "../lib/seo";

const HUBS = [
  { code: "VIC", name: "Victoria", cities: ["Docklands (Head office)", "Melbourne CBD", "Brunswick", "Richmond", "Box Hill", "Footscray", "Essendon"], landing: "melbourne", isHub: true },
  { code: "WA", name: "Western Australia", cities: ["West Perth", "Perth CBD", "Joondalup", "Fremantle", "Rockingham"], landing: "perth", isHub: true },
];
const SERVICED = [
  { code: "NSW", name: "New South Wales", cities: ["Sydney", "Parramatta", "Newcastle", "Wollongong"], landing: "sydney" },
  { code: "QLD", name: "Queensland", cities: ["Brisbane", "Gold Coast", "Sunshine Coast"], landing: "brisbane" },
  { code: "SA", name: "South Australia", cities: ["Adelaide", "Norwood", "Port Adelaide"], landing: "adelaide" },
  { code: "ACT", name: "Australian Capital Territory", cities: ["Canberra", "Belconnen"], landing: "canberra" },
  { code: "TAS", name: "Tasmania", cities: ["Hobart", "Launceston"], landing: "hobart" },
  { code: "NT", name: "Northern Territory", cities: ["Darwin", "Palmerston"], landing: "darwin" },
];

export default function LocationsPage() {
  useSEO({
    title: "NDIS Locations | Alpha Community Care | Australia-wide",
    description: "Alpha Community Care services participants across Australia — with active hubs in Melbourne (VIC) and Perth (WA), and national coordination capability.",
    canonical: `${SITE_URL}/locations`,
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Locations", path: "/locations" }])],
  });

  return (
    <div data-testid="page-locations">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-14 pb-10">
          <div className="alpha-overline">Locations</div>
          <h1 className="mt-3 text-4xl sm:text-[52px] font-display font-medium tracking-tight leading-[1.05]">Servicing participants across Australia.</h1>
          <p className="mt-4 max-w-2xl text-[color:var(--alpha-mute)]">Alpha Community Care operates active hubs in Melbourne and Perth, and supports participants and referral partners nationally — with active service capability and growing reach across all states and territories.</p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <a href="tel:1300380692" className="alpha-btn-primary !py-2 !px-4"><Phone className="w-4 h-4" /> 1300 380 692</a>
            <a href="mailto:hello@alphacommunitycare.com.au" className="alpha-btn-green !py-2 !px-4"><Mail className="w-4 h-4" /> hello@alphacommunitycare.com.au</a>
          </div>
        </div>
      </section>

      <section className="alpha-section">
        <div className="alpha-container">
          <div className="alpha-overline">Active hubs</div>
          <h2 className="mt-3 font-display text-3xl sm:text-[40px] font-medium">Where we operate from.</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {HUBS.map((s) => (
              <Link key={s.code} to={`/sil-${s.landing}`} className="alpha-card p-8 group" data-testid={`loc-${s.code}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl alpha-gradient text-white flex items-center justify-center font-display text-lg font-semibold">{s.code}</div>
                  <div>
                    <div className="font-display text-2xl font-semibold">{s.name}</div>
                    <div className="text-xs text-[color:var(--alpha-green-700)] font-semibold mt-0.5">Active hub</div>
                  </div>
                </div>
                <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm text-[color:var(--alpha-mute)]">
                  {s.cities.map((c) => (
                    <li key={c} className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {c}</li>
                  ))}
                </ul>
                <div className="mt-6 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:underline">NDIS support in {s.name} →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container">
          <div className="alpha-overline">Serviced nationally</div>
          <h2 className="mt-3 font-display text-3xl sm:text-[40px] font-medium">Active service capability across Australia.</h2>
          <p className="mt-3 text-[color:var(--alpha-mute)] max-w-3xl">Through our national coordination model, we support participants and referral partners across every state and territory. Contact intake to discuss capacity in your area.</p>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICED.map((s) => (
              <Link key={s.code} to={`/sil-${s.landing}`} className="alpha-card p-6 group" data-testid={`loc-${s.code}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[color:var(--alpha-purple-100)] text-[color:var(--alpha-purple-700)] flex items-center justify-center font-display font-semibold">{s.code}</div>
                  <div>
                    <div className="font-display text-lg font-semibold">{s.name}</div>
                  </div>
                </div>
                <ul className="mt-4 space-y-1 text-sm text-[color:var(--alpha-mute)]">
                  {s.cities.map((c) => (
                    <li key={c} className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {c}</li>
                  ))}
                </ul>
                <div className="mt-4 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:underline">NDIS support in {s.name} →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
